import React from 'react';
import { useEditorStore } from '../../store/editorStore';
import { SYMBOL_FAMILIES, SYMBOL_GROUPS, canonicalSymbolId, familyOf } from '../../symbols';
import { getSymbolPrimitives } from '../../symbolGeometry';
import type { SymbolFamily, SymbolVariation } from '../../symbols';
import type { SymbolId } from '../../types';

function polylinePoints(pts: number[]): string {
  const out: string[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) out.push(`${pts[i]},${pts[i + 1]}`);
  return out.join(' ');
}

/** Tiny inline SVG preview of a symbol, drawn from the same geometry as the canvas. */
export const SymbolThumb: React.FC<{ id: SymbolId; box?: number }> = ({ id, box = 40 }) => {
  const prims = getSymbolPrimitives(id, box, box);
  return (
    <svg width={box} height={box} viewBox={`-2 -2 ${box + 4} ${box + 4}`} style={{ overflow: 'visible' }}>
      {prims.map((p, i) => {
        const stroke = '#cfcfe0';
        const sw = 1.4;
        if (p.k === 'hatch') return null;
        if (p.k === 'line') {
          return <polyline key={i} points={polylinePoints(p.pts)} fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" />;
        }
        if (p.k === 'rect') {
          return <rect key={i} x={p.x} y={p.y} width={p.w} height={p.h} rx={p.r ?? 0} fill="none" stroke={stroke} strokeWidth={sw} />;
        }
        if (p.k === 'circle') {
          return <circle key={i} cx={p.x} cy={p.y} r={p.r} fill="none" stroke={stroke} strokeWidth={sw} />;
        }
        if (p.k === 'text') {
          return (
            <text key={i} x={p.x} y={p.y + p.size * 0.36} fontSize={p.size} fill={stroke} textAnchor="middle" fontFamily="system-ui, sans-serif">
              {p.text}
            </text>
          );
        }
        return <ellipse key={i} cx={p.x} cy={p.y} rx={p.rx} ry={p.ry} fill="none" stroke={stroke} strokeWidth={sw} />;
      })}
    </svg>
  );
};

export const SymbolVariationRow: React.FC<{
  family: SymbolFamily;
  activeId: SymbolId;
  onPick: (variation: SymbolVariation) => void;
}> = ({ family, activeId, onPick }) => {
  if (family.variations.length < 2) return null;
  const current = canonicalSymbolId(activeId);
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6a6a85' }}>
        {family.label} variations
      </p>
      <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(58px, 1fr))' }}>
        {family.variations.map((variation) => {
          const active = variation.id === current;
          return (
            <button
              key={variation.id}
              type="button"
              onClick={() => onPick(variation)}
              className="flex flex-col items-center gap-0.5 p-1 rounded-md border"
              style={{
                borderColor: active ? '#FF4C4C' : '#2e2e40',
                backgroundColor: active ? 'rgba(255,76,76,0.1)' : '#1c1c28',
              }}
              title={variation.label}
            >
              <SymbolThumb id={variation.id} box={28} />
              <span className="text-[9px] leading-tight text-center" style={{ color: active ? '#FF4C4C' : '#8888aa' }}>
                {variation.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SymbolPanel: React.FC = () => {
  const { pendingSymbolId, setPendingSymbol, setActiveTool } = useEditorStore();

  const pick = (id: SymbolId) => {
    setPendingSymbol(id);
    setActiveTool('symbol');
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold" style={{ color: '#f0f0f5' }}>
          Symbols
        </h3>
        <p className="text-xs mt-1" style={{ color: '#8888aa' }}>
          Pick a symbol, then click the canvas. Doors and fixtures can be mirrored, dragged, and copied.
        </p>
      </div>

      {SYMBOL_GROUPS.map((group) => {
        const items = SYMBOL_FAMILIES.filter((family) => family.group === group.id);
        if (!items.length) return null;
        const activeFamily = pendingSymbolId ? familyOf(pendingSymbolId) : undefined;
        const groupActive = activeFamily?.group === group.id ? activeFamily : undefined;
        return (
          <div key={group.id} className="space-y-2">
            <h4 className="text-[11px] uppercase tracking-wide" style={{ color: '#6a6a85' }}>
              {group.label}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {items.map((family) => {
                const active = activeFamily?.id === family.id;
                const thumbId = active && pendingSymbolId ? canonicalSymbolId(pendingSymbolId) : family.variations[0].id;
                return (
                  <button
                    key={family.id}
                    onClick={() => pick(active && pendingSymbolId ? canonicalSymbolId(pendingSymbolId) : family.variations[0].id)}
                    className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg border transition-colors"
                    style={{
                      borderColor: active ? '#FF4C4C' : '#2e2e40',
                      backgroundColor: active ? 'rgba(255,76,76,0.1)' : '#252532',
                    }}
                    title={family.label}
                  >
                    <SymbolThumb id={thumbId} />
                    <span className="text-[11px]" style={{ color: active ? '#FF4C4C' : '#8888aa' }}>
                      {family.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {groupActive && pendingSymbolId && (
              <SymbolVariationRow
                family={groupActive}
                activeId={pendingSymbolId}
                onPick={(variation) => pick(variation.id)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SymbolPanel;
